/**
 * `ListContext` — provides the parent list-hook's `queryKey` + `rowKey` to
 * any per-field hooks (`<listHookName>Field`) rendered inside `<Items>`.
 *
 * The codegen-emitted `<listHookName>Field` hooks call
 * `useContext(ListContext)` to discover which cache entry they should
 * attach to. This lets a leaf component like:
 *
 *     function Volume({ pubkey }) {
 *       const value = useGetMarketsField(pubkey, 'volume24h');
 *       return <span>€{value}</span>;
 *     }
 *
 * work without the consumer threading queryKey through props.
 *
 * Scope: per-field hooks ONLY work inside `<Items>`. On detail pages or
 * non-list surfaces, consumers use the destructured result of the detail
 * hook directly.
 */

import type { QueryKey } from '@tanstack/react-query';
import { createContext, type RefCallback } from 'react';
import type {
  EntityProjectionSpec,
  NestedEntityProjectionSpec,
} from '../internal/entity-projection.js';
import type { ViewerOverlaySpec } from '../internal/viewer-overlay.js';

export interface ListContextValue {
  readonly queryKey: QueryKey;
  readonly rowKey: string;
  /**
   * Curried ref factory for the row's entity id. Optional because the
   * `<Items>` render-prop provides the ref directly via its callback —
   * only the FlashList/SectionList path (where the consumer can't
   * receive a per-row ref through `renderItem`) needs to look it up
   * from context. Surfaced by the list hook's `ListProvider` component.
   */
  readonly itemObserver?: (id: string) => RefCallback<Element>;
  /**
   * MOR-859 — set for a normalized list hook. Builds the per-entity cache key
   * `['<entity>','entity', id]`; when present, leaf `useGet<Entity>Field`
   * hooks read the row from that entity query instead of scanning the feed's
   * id-only `data`. Absent on classic (full-row) feeds.
   */
  readonly entityQueryKey?: (id: string) => readonly unknown[];
  readonly entityProjection?: EntityProjectionSpec;
  readonly nestedEntityProjection?: NestedEntityProjectionSpec;
  /** Viewer-only row fields and the scope used to resolve their private key. */
  readonly viewerOverlay?: ViewerOverlaySpec;
  readonly viewerArgs?: Record<string, unknown>;
  readonly viewerScope?: string;
}

export const ListContext = createContext<ListContextValue | null>(null);
